<script>
import NavigationWrapper from "@/components/NavigationWrapper";
import ChangePaymentMethodModal from "@/components/ChangePaymentMethodModal";
import { mapState, mapActions, mapMutations, mapGetters } from "vuex";

export default {
  components: {
    NavigationWrapper,
    ChangePaymentMethodModal,
  },
  data() {
    return {
      payment_methods: [],
      default_pm: {},
      loading: true,
    };
  },
  async created() {
    if (!this.activeTeam.billing_id) {
      await this.$router.push({ path: "/" });
    } else {
      // Get Stripe Invoices for this Team
      await this.getInvoices();
      // Get the current default payment option for this stripe customer aka team (either bank account or credit card)
      await this.getDefaultPaymentMethod();
      // Get all of the contracts for this team (returned in reverse order of creatin AKA most recently created first)
      await this.getTeamContracts();
      // Get ALL payment methods on this stripe customer aka team
      await this.getAllPaymentMethods();
      this.payment_methods = this.existing_payment_methods;
      this.default_pm = this.default_payment_method;
      this.loading = false;
    }
  },
  computed: {
    ...mapState([
      "invoices",
      "default_payment_method",
      "existing_payment_methods",
      "teamContracts",
    ]),
    ...mapGetters(["activeTeam"]),
  },
  methods: {
    ...mapActions([
      "getInvoices",
      "getDefaultPaymentMethod",
      "getTeamContracts",
      "getAllPaymentMethods",
      "changeDefaultPaymentMethod",
    ]),
    ...mapMutations(["pushToast"]),
    // Format epoch timestamps for stripe invoices into human readable format
    date(epoch) {
      let date = new Date(epoch * 1000);
      return date.toDateString();
    },
    // format what is shown for the payment method depending on if it is a bank account or a "payment_method" aka credit card
    paymentType(default_payment_method) {
      if (default_payment_method.object == "bank_account") {
        return `${this.default_payment_method.bank_name} Bank Account ending in ${this.default_payment_method.last4}`;
      } else if (default_payment_method.object == "payment_method") {
        let name = this.default_payment_method.card.brand;
        let nameCapitalized = name.charAt(0).toUpperCase() + name.slice(1);
        return `${nameCapitalized} Credit Card ending in ${this.default_payment_method.card.last4} (Exp. ${this.default_payment_method.card.exp_month}/${this.default_payment_method.card.exp_year})`;
      }
    },
    // Hide the main change PM modal
    async hide() {
      await this.getAllPaymentMethods();
      let new_default = await this.getDefaultPaymentMethod();
      this.default_pm = new_default.result.payment_method;
      this.pushToast({
        title: "Changed Payment Method",
        message: "You have successfully changed your payment method.",
        variant: "success",
      });
      await this.$bvModal.hide("stripeModal");
    },
    // Show the change payment method modal
    show() {
      this.$bvModal.show("stripeModal");
    },
    // Set the default payment method to a new one
    async setDefault(pm) {
      let result = await this.changeDefaultPaymentMethod({ pm });
      if (result.ok) {
        await this.getAllPaymentMethods();
        // onSuccess: reset the default PM so the page displays new one
        let new_default = await this.getDefaultPaymentMethod();
        this.default_pm = new_default.result.payment_method;
        this.pushToast({
          title: "Changed Payment Method",
          message: "You have successfully changed your payment method.",
          variant: "success",
        });
        await this.$bvModal.hide("stripeModal");
      }
    },
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item active>Contracts and Invoices</b-breadcrumb-item>
    </template>
    <b-row v-if="loading">
      <b-col class="text-center mt-5">
        <div class="mb-4">
          <b-spinner
            style="width: 4rem; height: 4rem"
            variant="primary"
            label="Loading Workers..."
          ></b-spinner>
        </div>
        <b>Loading Billing Portal...</b>
      </b-col>
    </b-row>
    <div v-else-if="!loading">
      <b-row class="mb-2">
        <b-col>
          <h1>Contracts</h1>
          <b-card>
            <b-table-simple :sticky-header="true" responsive striped hover>
              <b-thead>
                <b-th>Contract No.</b-th>
                <b-th>Device</b-th>
                <b-th>Quantity</b-th>
                <b-th>$/kWh</b-th>
                <b-th>Term</b-th>
                <b-th>Expiration</b-th>
              </b-thead>
              <b-tbody>
                <b-tr
                  v-for="(contract, index) in this.teamContracts"
                  :key="index"
                >
                  <b-td>{{ contract.batch_number }}</b-td>
                  <b-td
                    v-text="
                      contract.device_type
                        ? contract.device_type.name
                        : 'No Device Found'
                    "
                  ></b-td>
                  <b-td>{{ contract.machine_count }}</b-td>
                  <b-td>${{ contract.rate_per_kWh }}</b-td>
                  <b-td>{{ contract.term_length }} months</b-td>
                  <b-td>{{ contract.expiration }}</b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </b-card>
          <div class="spacer" />
          <b-card>
            <b-row>
              <b-col>
                <b-card-text>
                  <h4>Payment Method</h4>
                  <div>
                    {{ this.paymentType(this.default_pm) }}
                  </div>
                </b-card-text>
              </b-col>
              <b-col>
                <b-button variant="primary" class="button" @click="show">
                  Change Payment Method
                </b-button>
              </b-col>
            </b-row>
          </b-card>
          <ChangePaymentMethodModal
            @newPM="hide"
            @setPM="setDefault"
            :payment_methods="this.payment_methods"
            :default="this.default_pm"
            id="stripeModal"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <h1>Invoices</h1>
          <b-table-simple :sticky-header="true" responsive striped hover>
            <b-thead>
              <b-th>Date Invoiced</b-th>
              <b-th>Invoice No.</b-th>
              <b-th>Description</b-th>
              <b-th>Total Charge</b-th>
              <b-th>Status</b-th>
            </b-thead>
            <b-tbody>
              <b-tr v-for="(invoice, index) in this.invoices" :key="index">
                <b-td>{{ date(invoice.created) }}</b-td>
                <b-td>
                  <b-link :href="`${invoice.invoice_pdf}`">
                    {{ invoice.number }}
                  </b-link>
                </b-td>
                <b-td>{{ invoice.metadata.description }}</b-td>
                <b-td>${{ invoice.total / 100 }}</b-td>
                <b-td>{{ invoice.status.toUpperCase() }}</b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-col>
      </b-row>
    </div>
  </NavigationWrapper>
</template>

<style scoped>
.spacer {
  height: 20px;
}
.contract {
  height: 200px;
}
.button {
  float: right;
}
</style>
